import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, openAuthedFile } from "@/api/client";
import { SUPPLIER_INVOICE_STATUS_LABELS, type SupplierInvoiceStatus } from "@ckgo2/shared/types";
import { formatPrice, formatDate } from "@ckgo2/shared/utils";
import { TrendingUp, ArrowDownCircle, ArrowUpCircle, FileDown, Loader2 } from "lucide-react";

interface ArRow {
  id: string;
  number: string;
  orderId?: string | null;
  currency: string;
  total: number;
  paid: number;
  outstanding: number;
  dueDate?: string | null;
  overdue: boolean;
}
interface ApRow {
  id: string;
  number?: string | null;
  supplierName?: string | null;
  amount: number;
  currency: string;
  dueDate?: string | null;
  status: SupplierInvoiceStatus;
  overdue: boolean;
}
interface Summary {
  /** Base reporting currency the totals are rolled up to (configured in Settings → Finance). */
  currency: string;
  outstanding: number;
  overdue: number;
  count: number;
}

function SummaryCard({
  label,
  value,
  sub,
  icon,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  icon: React.ReactNode;
  tone: string;
}) {
  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
          {sub && <p className="mt-1 text-sm text-gray-500">{sub}</p>}
        </div>
        <div className={`rounded-full p-3 ${tone}`}>{icon}</div>
      </div>
    </div>
  );
}

export default function FinancePage() {
  const [exporting, setExporting] = useState(false);
  const year = new Date().getFullYear();
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(`${year}-12-31`);

  const { data: ar } = useQuery({
    queryKey: ["finance-ar"],
    queryFn: () => api.get<{ data: ArRow[]; summary: Summary }>("/api/admin/finance/ar"),
  });
  const { data: ap } = useQuery({
    queryKey: ["finance-ap"],
    queryFn: () => api.get<{ data: ApRow[]; summary: Summary }>("/api/admin/finance/ap"),
  });

  const arRows = ar?.data ?? [];
  const apRows = ap?.data ?? [];

  const exportPohoda = async () => {
    setExporting(true);
    try {
      await openAuthedFile(
        `/api/admin/finance/export-pohoda?from=${from}&to=${to}`,
        `pohoda-${from}_${to}.xml`,
        { method: "POST" },
      );
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <TrendingUp className="h-6 w-6" /> Payments &amp; margin
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Receivables, payables and the Pohoda export
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-xs text-gray-500">
            From
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="input ml-1 text-sm"
            />
          </label>
          <label className="text-xs text-gray-500">
            To
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="input ml-1 text-sm"
            />
          </label>
          <button onClick={exportPohoda} disabled={exporting} className="btn-primary btn-sm">
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileDown className="h-4 w-4" />
            )}
            Pohoda XML
          </button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label="Receivables"
          value={formatPrice(ar?.summary.outstanding ?? 0, ar?.summary.currency ?? "CZK")}
          sub={`${ar?.summary.count ?? 0} open invoices`}
          icon={<ArrowDownCircle className="h-6 w-6" />}
          tone="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
        />
        <SummaryCard
          label="Overdue (AR)"
          value={formatPrice(ar?.summary.overdue ?? 0, ar?.summary.currency ?? "CZK")}
          icon={<ArrowDownCircle className="h-6 w-6" />}
          tone="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
        />
        <SummaryCard
          label="Payables"
          value={formatPrice(ap?.summary.outstanding ?? 0, ap?.summary.currency ?? "CZK")}
          sub={`${ap?.summary.count ?? 0} supplier invoices`}
          icon={<ArrowUpCircle className="h-6 w-6" />}
          tone="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
        />
        <SummaryCard
          label="Overdue (AP)"
          value={formatPrice(ap?.summary.overdue ?? 0, ap?.summary.currency ?? "CZK")}
          icon={<ArrowUpCircle className="h-6 w-6" />}
          tone="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* AR */}
        <div className="card">
          <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
            Accounts receivable
          </h2>
          {arRows.length === 0 ? (
            <p className="py-3 text-sm text-gray-400">Nothing outstanding 🎉</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {arRows.map((r) => (
                <li key={r.id} className="flex items-center justify-between py-2.5">
                  <div>
                    {r.orderId ? (
                      <Link
                        to={`/orders/${r.orderId}`}
                        className="text-primary-600 dark:text-primary-400 font-mono text-sm hover:underline"
                      >
                        {r.number}
                      </Link>
                    ) : (
                      <span className="font-mono text-sm">{r.number}</span>
                    )}
                    <p className={`text-xs ${r.overdue ? "text-red-500" : "text-gray-500"}`}>
                      {r.dueDate ? `Due ${formatDate(r.dueDate)}` : "No due date"}
                      {r.overdue ? " · overdue" : ""}
                    </p>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatPrice(r.outstanding, r.currency)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* AP */}
        <div className="card">
          <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
            Accounts payable
          </h2>
          {apRows.length === 0 ? (
            <p className="py-3 text-sm text-gray-400">Nothing to pay</p>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {apRows.map((r) => (
                <li key={r.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {r.supplierName ?? "Supplier"} {r.number ? `· ${r.number}` : ""}
                    </p>
                    <p className={`text-xs ${r.overdue ? "text-red-500" : "text-gray-500"}`}>
                      {SUPPLIER_INVOICE_STATUS_LABELS[r.status]}
                      {r.dueDate ? ` · due ${formatDate(r.dueDate)}` : ""}
                      {r.overdue ? " · overdue" : ""}
                    </p>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatPrice(r.amount, r.currency)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
